import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import api, { apiErr, fmtMoney } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Loader2, Gift as GiftIcon, Pencil, Trash2, Heart, Send, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";

const firstName = (n) => (n || "").trim().split(/\s+/)[0] || "there";
const thankPresets = (name) => {
  const f = firstName(name);
  return [
    { key: "heartfelt", label: "Heartfelt thanks", text: `${f}, thank you so much for your generosity. Your partnership means more than you know and directly fuels this mission. We're so grateful for you!` },
    { key: "prayer", label: "Thanks + prayer", text: `${f}, thank you for your gift! We're praying for you and your family today, and we're honored to have you walking this journey with us.` },
    { key: "welcome", label: "Welcome, first gift", text: `${f}, welcome to the team — and thank you for your very first gift! We're thrilled to have you partnering with us. This is the beginning of something special.` },
  ];
};

const emptyForm = { partner_id: "", partner_email: "", amount: "", currency: "USD", method: "Venmo", frequency: "one-time", note: "", date: "" };

export default function Gifts() {
  const [gifts, setGifts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | {mode:'create'} | {mode:'edit', id}
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [f, setF] = useState(emptyForm);
  const [thankGift, setThankGift] = useState(null);
  const [thankMsg, setThankMsg] = useState("");
  const [thankPush, setThankPush] = useState(true);
  const [thankEmail, setThankEmail] = useState(true);
  const [thankSending, setThankSending] = useState(false);

  const openThank = (g) => {
    setThankGift(g);
    setThankMsg(thankPresets(g.partner_name)[0].text);
    setThankPush(true); setThankEmail(true);
  };
  const sendThanks = async () => {
    if (!thankMsg.trim()) return toast.error("Write a short note first");
    if (!thankPush && !thankEmail) return toast.error("Pick at least one way to send");
    setThankSending(true);
    try {
      const { data } = await api.post(`/gifts/${thankGift.id}/thanks`, { message: thankMsg.trim(), push: thankPush, email: thankEmail });
      const ch = Object.entries(data.sent).filter(([, v]) => v).map(([k]) => k);
      if (ch.length) toast.success(`Thank-you sent via ${ch.join(" + ")} 💛`);
      else toast.warning("Marked as thanked, but nothing could be delivered");
      if (data.skipped && data.skipped.length) toast.message("Heads up", { description: data.skipped.join(" · ") });
      setThankGift(null); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setThankSending(false); }
  };

  const load = async () => {
    try {
      const [g, p] = await Promise.all([api.get("/gifts"), api.get("/partners")]);
      setGifts(g.data); setPartners(p.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setF(emptyForm); setDialog({ mode: "create" }); };
  const openEdit = (g) => {
    setF({
      partner_id: g.partner_id || "", partner_email: g.partner_id ? "" : (g.partner_email || ""),
      amount: g.amount_cents != null ? (g.amount_cents / 100).toString() : "",
      currency: g.currency || "USD", method: g.method || "", frequency: g.frequency || "one-time", note: g.note || "",
      date: g.date ? new Date(g.date).toISOString().slice(0, 10) : "",
    });
    setDialog({ mode: "edit", id: g.id });
  };

  const save = async () => {
    if (!f.amount) return toast.error("Enter an amount");
    setSaving(true);
    const payload = {
      partner_id: f.partner_id || undefined,
      partner_email: f.partner_email || undefined,
      amount_cents: Math.round(parseFloat(f.amount) * 100),
      currency: f.currency, method: f.method, frequency: f.frequency, note: f.note,
      date: f.date ? new Date(f.date + "T12:00:00").toISOString() : undefined,
    };
    try {
      if (dialog.mode === "edit") { await api.put(`/gifts/${dialog.id}`, payload); setDialog(null); toast.success("Gift updated"); load(); }
      else {
        const { data } = await api.post("/gifts", payload);
        setDialog(null);
        toast.success("Gift recorded");
        load();
        if (data && data.id) openThank(data);
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/gifts/${delTarget.id}`); setDelTarget(null); toast.success("Gift deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;
  const total = gifts.reduce((s, g) => s + (g.amount_cents || 0), 0);

  return (
    <div>
      <PageHeader title="Gifts" />
      <div className="flex justify-end mb-5">
        <Button data-testid="new-gift-btn" onClick={openCreate} className="rounded-full accent-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-1" /> Record</Button>
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">{dialog?.mode === "edit" ? "Edit gift" : "Record a gift"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm">Partner</Label>
              <Select value={f.partner_id} onValueChange={(v) => setF({ ...f, partner_id: v })}>
                <SelectTrigger data-testid="gift-partner-select" className="rounded-xl h-11 mt-1"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>
                  {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Or credit by email <span className="text-muted-foreground">(optional)</span></Label>
              <Input data-testid="gift-email-input" type="email" placeholder="supporter@email.com" className="rounded-xl h-11 mt-1" value={f.partner_email} onChange={(e) => setF({ ...f, partner_email: e.target.value })} />
              <p className="text-[11px] text-muted-foreground mt-1">If they're not in your list yet, this gift shows in their "Your Impact" the moment they join with this email.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Amount</Label><Input data-testid="gift-amount-input" type="number" className="rounded-xl h-11 mt-1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
              <div><Label className="text-sm">Method</Label><Input data-testid="gift-method-input" className="rounded-xl h-11 mt-1" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} /></div>
            </div>
            <div>
              <Label className="text-sm">Frequency</Label>
              <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}>
                <SelectTrigger data-testid="gift-frequency-select" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one-time">One-time gift</SelectItem>
                  <SelectItem value="monthly">Monthly supporter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea data-testid="gift-note-input" placeholder="Note (optional)" className="rounded-xl" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <div>
              <Label className="text-sm">Date received <span className="text-muted-foreground">(optional)</span></Label>
              <Input data-testid="gift-date-input" type="date" className="rounded-xl h-11 mt-1" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
              <p className="text-[11px] text-muted-foreground mt-1">Leave blank to use today. Back-date a gift so it lands on the day it was actually received.</p>
            </div>
            <Button data-testid="gift-submit-btn" disabled={saving} onClick={save} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? "Saving…" : dialog?.mode === "edit" ? "Save changes" : "Record gift"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_24px_rgba(44,44,62,0.04)] mb-5">
        <div className="text-sm text-muted-foreground">Total received</div>
        <div className="font-heading font-extrabold text-4xl text-[var(--brand-navy)]">{fmtMoney(total)}</div>
      </div>

      <div className="space-y-2">
        {gifts.length === 0 && <p className="text-sm text-muted-foreground">No gifts recorded yet.</p>}
        {gifts.map((g) => (
          <div key={g.id} className="bg-white rounded-xl p-4 flex items-center justify-between gap-3 shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="gift-item">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-xl bg-[var(--tenant-accent)]/15 flex items-center justify-center shrink-0"><GiftIcon className="h-4 w-4 accent-text" /></div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#2C2C3E] flex items-center gap-2 truncate">{g.partner_name || "Anonymous"}
                  {g.frequency === "monthly" && <span className="text-[10px] uppercase tracking-wide text-[var(--brand-navy)] bg-[var(--brand-navy)]/10 px-1.5 py-0.5 rounded-full shrink-0">Monthly</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5">{g.method}{g.date ? ` · ${format(new Date(g.date), "MMM d")}` : ""}
                  {g.thanked_at
                    ? <span data-testid="gift-thanked-badge" className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 font-medium"><CheckCircle2 className="h-3 w-3" /> Thanked</span>
                    : <span data-testid="gift-needs-thanks-badge" className="inline-flex items-center gap-0.5 text-[10px] accent-text font-medium"><Heart className="h-3 w-3" /> Needs thanks</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-heading font-bold text-[var(--brand-navy)] mr-1">{fmtMoney(g.amount_cents, g.currency)}</span>
              <button data-testid="gift-thanks-btn" onClick={() => openThank(g)} title="Say thanks" className={`h-8 w-8 rounded-full flex items-center justify-center ${g.thanked_at ? "text-muted-foreground hover:text-[var(--brand-navy)]" : "accent-bg text-white hover:opacity-90"}`}><Heart className="h-4 w-4" /></button>
              <button data-testid="gift-edit-btn" onClick={() => openEdit(g)} className="text-muted-foreground hover:text-[var(--brand-navy)]"><Pencil className="h-4 w-4" /></button>
              <button data-testid="gift-delete-btn" onClick={() => setDelTarget(g)} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="gift-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[var(--brand-navy)]">Delete this gift?</AlertDialogTitle>
            <AlertDialogDescription>
              {delTarget && `${fmtMoney(delTarget.amount_cents, delTarget.currency)} from ${delTarget.partner_name || "Anonymous"}`} will be removed and your total received will update. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="gift-delete-confirm" onClick={(e) => { e.preventDefault(); remove(); }} className="rounded-full bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!thankGift} onOpenChange={(v) => !v && setThankGift(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="thank-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[var(--brand-navy)] flex items-center gap-2"><Heart className="h-4 w-4 accent-text" /> Thank {firstName(thankGift?.partner_name)}</DialogTitle>
            <DialogDescription className="text-xs">A warm, personal note — delivered right inside your app and/or a branded email from your organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div className="flex flex-wrap gap-1.5">
              {thankPresets(thankGift?.partner_name).map((p) => (
                <button key={p.key} data-testid={`thank-preset-${p.key}`} onClick={() => setThankMsg(p.text)}
                  className="text-xs rounded-full border px-3 py-1.5 text-[var(--brand-navy)] hover:bg-[var(--brand-navy)]/5">{p.label}</button>
              ))}
            </div>
            <Textarea data-testid="thank-message-input" rows={5} value={thankMsg} onChange={(e) => setThankMsg(e.target.value)} className="rounded-xl" placeholder="Write a personal thank-you…" />
            <div className="rounded-xl border divide-y">
              <label className="flex items-center justify-between p-3 cursor-pointer">
                <span className="text-sm">Push notification</span>
                <Switch data-testid="thank-push-switch" checked={thankPush} onCheckedChange={setThankPush} />
              </label>
              <label className="flex items-center justify-between p-3 cursor-pointer">
                <span className="text-sm">Branded email</span>
                <Switch data-testid="thank-email-switch" checked={thankEmail} onCheckedChange={setThankEmail} />
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">We automatically respect each partner's notification and email preferences.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1 h-11" onClick={() => setThankGift(null)}>Not now</Button>
              <Button data-testid="thank-send-btn" disabled={thankSending} onClick={sendThanks} className="rounded-full flex-1 h-11 accent-bg text-white hover:opacity-90"><Send className="h-4 w-4 mr-1" /> {thankSending ? "Sending…" : "Send thanks"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
